import {
  DocumentAnalysisTimeline,
  DocumentAnalysisTimelineInfo,
} from '@interfaces/a-organizar/documentAnalysisTimeline';

export type DocumentAnalysisViewMode = 'table' | 'cards';

export interface DocumentAnalysisRow {
  id: string;
  companyName: string;
  cnpj: string;
  pendingDocuments: number;
  approvedDocuments: number;
  rejectedDocuments: number;
  totalDocuments: number;
}

export interface DocumentAnalysisTotals {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
  companies: number;
  pendingPercent: number;
  approvedPercent: number;
  rejectedPercent: number;
}

/**
 * Ordem das faixas do DTO. `upToZeroDays` está na interface TS mas a API nunca envia
 * (o backend joga dias 0 e 1 juntos em `upToOneDay`); fica na lista por segurança.
 */
const TIMELINE_BUCKETS: (keyof DocumentAnalysisTimeline)[] = [
  'upToZeroDays',
  'upToOneDay',
  'upToTwoDays',
  'upToThreeDays',
  'upToFourDays',
  'upToFiveDays',
  'upToSixDays',
  'sevenPlusDays',
];

function toRow(info: DocumentAnalysisTimelineInfo): DocumentAnalysisRow {
  return {
    id: info.id,
    companyName: info.companyName ?? '',
    cnpj: info.cnpj ?? '',
    pendingDocuments: info.pendingDocuments ?? 0,
    approvedDocuments: info.approvedDocuments ?? 0,
    rejectedDocuments: info.rejectedDocuments ?? 0,
    totalDocuments: info.totalDocuments ?? 0,
  };
}

/** Achata as faixas de tempo numa lista única, ordenada por pendentes desc. */
export function flattenTimeline(timeline: DocumentAnalysisTimeline | null): DocumentAnalysisRow[] {
  if (!timeline) return [];

  return TIMELINE_BUCKETS.flatMap(bucket => timeline[bucket] ?? [])
    .map(toRow)
    .sort((a, b) => b.pendingDocuments - a.pendingDocuments);
}

function toPercent(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

/** Agrega os KPIs do topo. Recebe sempre a lista completa, nunca a filtrada. */
export function buildTotals(rows: DocumentAnalysisRow[]): DocumentAnalysisTotals {
  const pending = rows.reduce((sum, row) => sum + row.pendingDocuments, 0);
  const approved = rows.reduce((sum, row) => sum + row.approvedDocuments, 0);
  const rejected = rows.reduce((sum, row) => sum + row.rejectedDocuments, 0);
  const total = rows.reduce((sum, row) => sum + row.totalDocuments, 0);

  return {
    pending,
    approved,
    rejected,
    total,
    companies: rows.length,
    pendingPercent: toPercent(pending, total),
    approvedPercent: toPercent(approved, total),
    rejectedPercent: toPercent(rejected, total),
  };
}

/** Busca por nome da empresa ou por CNPJ (ignora pontuação do CNPJ digitado). */
export function matchesSearch(row: DocumentAnalysisRow, term: string): boolean {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return true;

  if (row.companyName.toLowerCase().includes(normalized)) return true;

  const digits = normalized.replace(/\D/g, '');
  return digits.length > 0 && row.cnpj.replace(/\D/g, '').includes(digits);
}
